import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { PaperFilters } from '@/module/Author/Paper/model/PaperTypes';
import { PublicationNewAliasRequest } from '@/module/Author/Publication/model/PublicationTypes';
import { PublicationImpactFactor, PublicationInfluence } from '@/module/Journal';

import { Id, PagedParams, PagedResponse, getQueryKey, invalidateAfterDelete } from '@feature/utils';

import * as api from './AbstractApi';
import { Abstract, AbstractRequest } from './AbstractTypes';

const abstractKey = getQueryKey('abstract');

export function getAbstractImpactFactorOptions(abstractId: Id): UseSuspenseQueryOptions<PublicationImpactFactor> {
  return {
    queryKey: abstractKey.as([abstractId, 'metrics', 'impact-factor']),
    queryFn: () => api.getAbstractImpactFactor(abstractId),
  };
}

export function getAbstractInfluenceOptions(abstractId: Id): UseSuspenseQueryOptions<PublicationInfluence> {
  return {
    queryKey: abstractKey.as([abstractId, 'metrics', 'influence']),
    queryFn: () => api.getAbstractInfluence(abstractId),
  };
}

export function getAbstractPagedOptions(
  params: PagedParams<PaperFilters>,
): UseSuspenseQueryOptions<PagedResponse<Abstract>> {
  return {
    queryKey: abstractKey.paged(params),
    queryFn: () => api.getPaged(params),
  };
}

export function getAbstractOptions(abstractId: Id): UseSuspenseQueryOptions<Abstract> {
  return {
    queryKey: abstractKey.one(abstractId),
    queryFn: () => api.get(abstractId),
  };
}

export function useAbstractEdit(abstractId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: AbstractRequest) => api.edit(abstractId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: abstractKey.root(),
      }),
  });
}

export function useAbstractCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: AbstractRequest) => api.create(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: abstractKey.root(),
      }),
  });
}

export function useAbstractDelete(abstractId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteById(abstractId),
    onSuccess: () => invalidateAfterDelete(queryClient, abstractKey, abstractId),
  });
}

export function useAbstractAddNewAlias() {
  const queryClient = useQueryClient();
  return useMutation<Abstract, Error, { abstractId: Id; request: PublicationNewAliasRequest }>({
    mutationFn: ({ abstractId, request }) => api.addNewAlias(abstractId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: abstractKey.root(),
      }),
  });
}
